/** Minutes since midnight of a `HH:mm` time, or null when it is not one. */
export function timeToMinutes(time: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time ?? "");
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
}

export function pad2(value: number): string {
  return value.toString().padStart(2, "0");
}

/** `HH:mm` of minutes since midnight; wraps around the day in both directions. */
export function minutesToTime(totalMinutes: number): string {
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

/**
 * Reads a time the way people type it: `9`, `930`, `1430`, `14.30`, `14:30`, `14,30`, `14h30`, `14 30` and `14u`.
 * Returns `HH:mm`, null for an empty entry and false for something that is no time. Minutes stay exactly as typed:
 * the minute step only applies to the arrows and to Nu.
 */
export function parseTypedTime(raw: string | null | undefined): string | null | false {
  const text = String(raw ?? "").trim().toLowerCase().replace(/\s*u(?:ur)?$/, "");
  if (text === "") {
    return null;
  }
  let hours: number;
  let minutes: number;
  const separated = /^(\d{1,2})(?:\s*[:.,h]\s*|\s+)(\d{1,2})$/.exec(text);
  if (separated) {
    hours = Number(separated[1]);
    minutes = Number(separated[2]);
  } else if (/^\d{1,4}$/.test(text)) {
    if (text.length <= 2) {
      hours = Number(text);
      minutes = 0;
    } else if (text.length === 3) {
      hours = Number(text.slice(0, 1));
      minutes = Number(text.slice(1));
    } else {
      hours = Number(text.slice(0, 2));
      minutes = Number(text.slice(2));
    }
  } else {
    return false;
  }
  if (hours > 23 || minutes > 59) {
    return false;
  }
  return `${pad2(hours)}:${pad2(minutes)}`;
}

/** Rounds minutes since midnight up to the next multiple of the step (10:42 with step 5 becomes 10:45). */
export function roundUpToStep(totalMinutes: number, step: number): number {
  if (step <= 1) {
    return totalMinutes;
  }
  return Math.ceil(totalMinutes / step) * step;
}

/** The time of a date as `HH:mm`. */
export function timeOfDate(date: Date): string {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Nu: the current time rounded up to the minute step, so 10:42 with step 5 becomes 10:45. */
export function nowRoundedUp(step: number, now: Date = new Date()): string {
  return minutesToTime(Math.min(roundUpToStep(now.getHours() * 60 + now.getMinutes(), step), 1439));
}

/** Whether a time lies within the optional `HH:mm` bounds (both inclusive). */
export function isTimeWithin(time: string, minTime?: string | null, maxTime?: string | null): boolean {
  const value = timeToMinutes(time);
  if (value === null) {
    return false;
  }
  const min = timeToMinutes(minTime);
  const max = timeToMinutes(maxTime);
  return (min === null || value >= min) && (max === null || value <= max);
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function isSameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  return dayKey(a) === dayKey(b);
}

/** A day number that orders days, ignoring the time of day. */
export function dayKey(date: Date): number {
  return date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();
}

export interface RangePreset {
  /** Translation key under `date_picker.presets`, or a ready label for a custom preset. */
  label: string
  from: Date
  to: Date
}

/** Where a period field looks: back for filters on the past, forward for planning. */
export type RangePresetSet = "back" | "forward";

/** Snelle keuzes of a period field. Weeks start on Monday. */
export function rangePresets(set: RangePresetSet, today: Date = new Date()): RangePreset[] {
  const day = startOfDay(today);
  const monday = addDays(day, -((day.getDay() + 6) % 7));
  const year = day.getFullYear();
  const month = day.getMonth();
  const shared = {
    today: { label: "today", from: day, to: day },
    thisWeek: { label: "this_week", from: monday, to: addDays(monday, 6) },
    thisMonth: { label: "this_month", from: new Date(year, month, 1), to: new Date(year, month + 1, 0) },
  };
  if (set === "forward") {
    return [
      shared.today,
      { label: "tomorrow", from: addDays(day, 1), to: addDays(day, 1) },
      shared.thisWeek,
      { label: "next_week", from: addDays(monday, 7), to: addDays(monday, 13) },
      shared.thisMonth,
      { label: "next_month", from: new Date(year, month + 1, 1), to: new Date(year, month + 2, 0) },
      { label: "next_7_days", from: day, to: addDays(day, 6) },
      { label: "next_30_days", from: day, to: addDays(day, 29) },
    ];
  }
  return [
    shared.today,
    { label: "yesterday", from: addDays(day, -1), to: addDays(day, -1) },
    shared.thisWeek,
    { label: "last_week", from: addDays(monday, -7), to: addDays(monday, -1) },
    shared.thisMonth,
    { label: "last_month", from: new Date(year, month - 1, 1), to: new Date(year, month, 0) },
    { label: "last_7_days", from: addDays(day, -6), to: day },
    { label: "last_30_days", from: addDays(day, -29), to: day },
  ];
}

/** Short day and month, as in "23 okt"; with the year when asked. */
export function formatShortDate(date: Date, locale: string, withYear: boolean): string {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: withYear ? "numeric" : undefined,
  }).format(date);
}

/** Weekday, day, month and year, as in "vr 23 okt 2026"; without the year for a yearless field. */
export function formatFieldDate(date: Date, locale: string, yearless: boolean): string {
  if (yearless) {
    return formatShortDate(date, locale, false);
  }
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}
